import { LoadingButton } from "@mui/lab";
import { Alert, Box, Button, Grid2 as Grid, Stack, Tooltip } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AntSwitch, CollapsableHeader, HighlightSelectBox, MyPaper, SectionPaper, TextField } from "../../../components";
import { SelectBoxOption } from "../../../interfaces";
import { useAppDispatch, useAppSelector } from "../../../redux";
import { useUpdateQuoteMutation } from "../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../redux/slices/backdrop";
import { Address, AddressValue, Correspondance, CorrespondanceValue, QuoteSliceInterface, updateQuoteSlice } from "../../../redux/slices/quote";
import { AppRoutes } from "../../../router/routes";
import { preparePayload } from "../utils";
import { isValidPhoneNumber, ValidateEmail } from "../../../utils";

export const QuoteCorrespondance: React.FC<{ quoteId: string, readOnly?: boolean, setIsSaved: any, refetch: () => void }> = ({ quoteId, readOnly, setIsSaved, refetch }) => {

    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const { quote } = useAppSelector(store => store);

    const [updateQuote, updateQuoteData] = useUpdateQuoteMutation();

    const [state, setState] = React.useState<QuoteSliceInterface>(quote);


    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to Update Quote Cover Letter. Try Again Later!`;
        let errorData: any = updateQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        return message;
    };

    const saveItemDetails = () => {

        if (state.correspondance.soldBy.address.emailId.error) {
            enqueueSnackbar('Please fix email validation errors in sold by', { variant: "warning" });
            return;
        }

        if (state.correspondance.soldBy.address.phoneNumber.error) {
            enqueueSnackbar('Please fix phone number validation errors in sold by', { variant: "warning" });
            return;
        }

        if (state.correspondance.soldTo.address.emailId.error) {
            enqueueSnackbar('Please fix email validation errors in sold to', { variant: "warning" });
            return;
        }

        if (state.correspondance.soldTo.address.phoneNumber.error) {
            enqueueSnackbar('Please fix phone number validation errors in sold to', { variant: "warning" });
            return;
        }

        if (state.correspondance.shipFrom.address.emailId.error) {
            enqueueSnackbar('Please fix email validation errors in ship from', { variant: "warning" });
            return;
        }

        if (state.correspondance.shipFrom.address.phoneNumber.error) {
            enqueueSnackbar('Please fix phone number validation errors in ship from', { variant: "warning" });
            return;
        }

        if (state.correspondance.shipTo.address.emailId.error) {
            enqueueSnackbar('Please fix email validation errors in ship to', { variant: "warning" });
            return;
        }

        if (state.correspondance.shipTo.address.phoneNumber.error) {
            enqueueSnackbar('Please fix phone number validation errors in ship to', { variant: "warning" });
            return;
        }

        let payload = { ...preparePayload(state) };

        dispatch(openBackdrop('Updating....'))
        updateQuote({ id: quoteId, payload }).then((res: any) => {
            if (res.data) {
                setIsSaved(true);
                dispatch(updateQuoteSlice(state));
                enqueueSnackbar('Quote Correspondence Updated Successfully!', { variant: "success" });
            } else {
                refetch();
                console.log('error:', res.error)
                enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
            }
            dispatch(closeBackdrop())
        });

        setSearchParams({});
    };

    const updateQuoteCorrespondance = (payload: {
        key: keyof Correspondance, childKey?: keyof CorrespondanceValue, addressParentKey?: keyof Address, addressChildKey?: keyof AddressValue, value: any
    }) => {
        let newState: QuoteSliceInterface = copy(state);

        if (payload.addressParentKey === 'phoneNumber' && typeof payload.value === 'string') {
            const isInValid = !isValidPhoneNumber(payload.value);
            newState.correspondance[payload.key].address[payload.addressParentKey].error = isInValid;
            newState.correspondance[payload.key].address[payload.addressParentKey].errorMessage = isInValid ? "Enter a valid mobile no" : "";
        }

        if (payload.addressParentKey === 'emailId' && typeof payload.value === 'string') {
            const isInValid = !ValidateEmail(payload.value);
            newState.correspondance[payload.key].address[payload.addressParentKey].error = isInValid;
            newState.correspondance[payload.key].address[payload.addressParentKey].errorMessage = isInValid ? "Enter a valid email id" : "";
        }

        if (payload.addressParentKey && payload.addressChildKey) {
            newState.correspondance[payload.key].address[payload.addressParentKey][payload.addressChildKey] = payload.value as never;
        } else if (payload.childKey) {
            newState.correspondance[payload.key][payload.childKey] = payload.value as never;
        }

        setState({ ...state, ...newState });
        setIsSaved(false);
    };

    const onAddressTypeChanged = (checked: boolean, key: keyof Correspondance) => {
        updateQuoteCorrespondance({ key, childKey: 'include', value: checked });
    };

    const onAddressItemChanged = (value: boolean | string, key: keyof Correspondance, addressParentKey: keyof Address, addressChildKey: keyof AddressValue) => {
        updateQuoteCorrespondance({ key, childKey: 'address', addressParentKey, addressChildKey, value: value as never });
    };

    const fillFromExistingAddress = (to: keyof Correspondance, from: keyof Correspondance) => {

        const newQuote = copy(state);

        newQuote.correspondance[to] = {
            include: true,
            address: {
                name: {
                    include: newQuote.correspondance[from].address.name.include,
                    value: newQuote.correspondance[from].address.name.value,
                    error: newQuote.correspondance[from].address.name.error,
                    errorMessage: newQuote.correspondance[from].address.name.errorMessage
                },
                emailId: {
                    include: newQuote.correspondance[from].address.emailId.include,
                    value: newQuote.correspondance[from].address.emailId.value,
                    error: newQuote.correspondance[from].address.emailId.error,
                    errorMessage: newQuote.correspondance[from].address.emailId.errorMessage
                },
                phoneNumber: {
                    include: newQuote.correspondance[from].address.phoneNumber.include,
                    value: newQuote.correspondance[from].address.phoneNumber.value,
                    error: newQuote.correspondance[from].address.phoneNumber.error,
                    errorMessage: newQuote.correspondance[from].address.phoneNumber.errorMessage
                },
                address: {
                    include: newQuote.correspondance[from].address.address.include,
                    value: newQuote.correspondance[from].address.address.value,
                    error: newQuote.correspondance[from].address.address.error,
                    errorMessage: newQuote.correspondance[from].address.address.errorMessage
                }
            }
        }

        setState({ ...state, ...newQuote });
        setIsSaved(false);
    };

    React.useEffect(() => {
        setState(quote);
        // eslint-disable-next-line
    }, [quote]);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            saveItemDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    React.useEffect(() => {
        setIsSaved(true);
        // eslint-disable-next-line
    }, []);

    return (
        (<Stack sx={{
            gap: 1
        }}>
            <SectionPaper title="Correspondence" isExpand={false} hideExpandCollapse={true}>
                <></>
            </SectionPaper>
            <Grid container spacing={2}>
                {/* Sold By */}
                <Grid
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <CorrespondanceAddress
                        readOnly={readOnly}
                        address={state.correspondance.soldBy}
                        addressType="Sold By"
                        onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'soldBy')}
                        onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'soldBy', key, childKey)}
                    />
                </Grid>

                {/* Sold To */}
                <Grid
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <CorrespondanceAddress
                        readOnly={readOnly}
                        address={state.correspondance.soldTo}
                        addressType="Sold To"
                        onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'soldTo')}
                        onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'soldTo', key, childKey)}
                    />
                </Grid>

                {false && <>
                    {/* Bill From */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <CorrespondanceAddress
                            readOnly={readOnly}
                            address={state.correspondance.billFrom}
                            addressType="Bill From"
                            onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'billFrom')}
                            onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'billFrom', key, childKey)}
                            showFillFrom={state.correspondance.billFrom.include}
                            fillFromOptions={[{ value: 'soldBy', label: 'Sold By', disabled: !state.correspondance.soldBy.include }]}
                            onFillFromChanged={(option) => option ? fillFromExistingAddress('billFrom', option.value as keyof Correspondance) : false}
                        />
                    </Grid>

                    {/* Bill To */}
                    <Grid
                        size={{
                            xs: 12,
                            sm: 6
                        }}>
                        <CorrespondanceAddress
                            readOnly={readOnly}
                            address={state.correspondance.billTo}
                            addressType="Bill To"
                            onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'billTo')}
                            onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'billTo', key, childKey)}
                            showFillFrom={state.correspondance.billTo.include}
                            fillFromOptions={[{ value: 'soldTo', label: 'Sold To', disabled: !state.correspondance.soldTo.include }]}
                            onFillFromChanged={(option) => option ? fillFromExistingAddress('billTo', option.value as keyof Correspondance) : false}
                        />
                    </Grid>
                </>}

                {/* Ship From */}
                <Grid
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <CorrespondanceAddress
                        readOnly={readOnly}
                        address={state.correspondance.shipFrom}
                        addressType="Ship From"
                        onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'shipFrom')}
                        onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'shipFrom', key, childKey)}
                        showFillFrom={state.correspondance.shipFrom.include}
                        fillFromOptions={
                            [
                                { value: 'soldBy', label: 'Sold By', disabled: !state.correspondance.soldBy.include },
                                // { value: 'billFrom', label: 'Bill From', disabled: !state.correspondance.billFrom.include }
                            ]
                        }
                        onFillFromChanged={(option) => option ? fillFromExistingAddress('shipFrom', option.value as keyof Correspondance) : false}
                    />
                </Grid>

                {/* Ship To */}
                <Grid
                    size={{
                        xs: 12,
                        sm: 6
                    }}>
                    <CorrespondanceAddress
                        readOnly={readOnly}
                        address={state.correspondance.shipTo}
                        addressType="Ship To"
                        onAddressTypeChanged={(checked) => onAddressTypeChanged(checked, 'shipTo')}
                        onAddressItemChanged={(key, childKey, value) => onAddressItemChanged(value, 'shipTo', key, childKey)}
                        showFillFrom={state.correspondance.shipTo.include}
                        fillFromOptions={
                            [
                                { value: 'soldTo', label: 'Sold To', disabled: !state.correspondance.soldTo.include },
                                // { value: 'billTo', label: 'Bill To', disabled: !state.correspondance.billTo.include }
                            ]
                        }
                        onFillFromChanged={(option) => option ? fillFromExistingAddress('shipTo', option.value as keyof Correspondance) : false}
                    />
                </Grid>

                {/* Alert Box */}
                <Grid size={12}>
                    {(updateQuoteData.isError) && <Alert sx={{ mt: 2 }} severity="error">{giveMeErrorMessage()}</Alert>}
                </Grid>
            </Grid>
            {
                !readOnly && <MyPaper padding={0} height={"70px"}>
                    <Box sx={{ paddingX: 2 }}>

                        <Stack
                            direction={"row"}
                            spacing={1}
                            sx={{
                                alignItems: "center",
                                justifyContent: "center",
                                p: 2
                            }}>
                            {/* Cancel Button */}
                            <Button disabled={updateQuoteData.isLoading} variant="outlined" sx={{ width: 200 }} color="primary" onClick={() => navigate(AppRoutes.allQuickQuotes)} >
                                Cancel
                            </Button>

                            {/* Create Button */}
                            <LoadingButton loading={updateQuoteData.isLoading} variant="contained" sx={{ width: 200 }} color="primary" onClick={saveItemDetails}>
                                {"Save"}
                            </LoadingButton>
                        </Stack>
                    </Box>
                </MyPaper>
            }
        </Stack>)
    );
}

export const CorrespondanceAddress: React.FC<{
    address: CorrespondanceValue,
    addressType: string,
    onAddressTypeChanged: (checked: boolean) => void,
    onAddressItemChanged: (key: keyof Address, childKey: keyof AddressValue, value: boolean | string) => void,
    showFillFrom?: boolean,
    fillFromOptions?: SelectBoxOption[],
    onFillFromChanged?: (option: SelectBoxOption) => void,
    readOnly?: boolean
}> = (props) => {

    const {
        address, addressType,
        onAddressTypeChanged, onAddressItemChanged,
        showFillFrom, fillFromOptions, onFillFromChanged, readOnly = false
    } = props;

    const [fillFrom, setFillForm] = React.useState<SelectBoxOption | null>(null)

    const addressStack: { title: string, key: keyof Address, type: string, value?: string, isEnabled: boolean, error?: boolean, errorMessage?: string }[] = [
        { title: "Name", key: "name", type: "text", value: address.address.name.value, isEnabled: address.address.name.include, error: address.address.name.error, errorMessage: address.address.name.errorMessage },
        { title: "Email ID", key: "emailId", type: "email", value: address.address.emailId.value, isEnabled: address.address.emailId.include, error: address.address.emailId.error, errorMessage: address.address.emailId.errorMessage },
        { title: "Phone Number", key: "phoneNumber", type: "tel", value: address.address.phoneNumber.value, isEnabled: address.address.phoneNumber.include, error: address.address.phoneNumber.error, errorMessage: address.address.phoneNumber.errorMessage },
        { title: "Address", key: "address", type: "text", value: address.address.address.value, isEnabled: address.address.address.include, error: address.address.address.error, errorMessage: address.address.address.errorMessage },
    ];

    return (
        <MyPaper padding={0}>
            <CollapsableHeader
                disabled={readOnly}
                title={addressType}
                showIcon={true}
                isExpanded={true}
                showSwitch={true}
                switchStatus={address.include}
                onSwitchChanged={(checked) => onAddressTypeChanged(checked)}
            >
                <Stack spacing={2} sx={{
                    p: 2
                }}>
                    {showFillFrom && <HighlightSelectBox
                        margin="none"
                        fullWdith
                        options={fillFromOptions ?? []}
                        size="small"
                        label="Fill From"
                        value={fillFrom}
                        variant="filled"
                        onChange={(option: SelectBoxOption) => {
                            setFillForm(option)
                            if (onFillFromChanged)
                                onFillFromChanged(option)
                        }}
                        viewOnly={readOnly}
                    />}

                    {addressStack.map((stack) => <Stack
                        direction={"row"}
                        spacing={1}
                        key={stack.key}
                        sx={{
                            alignItems: "center",
                            position: "relative"
                        }}>
                        <TextField
                            size="small"
                            fullWidth
                            variant="outlined"
                            value={stack.value}
                            type={stack.type}
                            label={stack.title}
                            placeholder={`Enter ${stack.title}`}
                            multiline={stack.key === 'address'}
                            rows={5}
                            error={stack.error}
                            helperText={stack.errorMessage}
                            onChange={(e) => onAddressItemChanged(stack.key, 'value', e.target.value)}
                            viewOnly={readOnly}
                        />

                        <Box sx={{ position: "absolute", right: "8px", top: stack.key === 'address' ? "8px" : `${stack.error ? '12px' : 'unset'}` }}>
                            <Tooltip title={stack.isEnabled ? "Disable to hide in the Quote Preview" : "Enable to show in the Quote Preview"} arrow placement="top">
                                <AntSwitch
                                    checked={stack.isEnabled}
                                    onChange={(e, checked) =>
                                        onAddressItemChanged(stack.key, 'include', checked)
                                    }
                                    disabled={readOnly}
                                />
                            </Tooltip>
                        </Box>
                    </Stack>)}
                </Stack>
            </CollapsableHeader>
        </MyPaper>
    );
}